import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

// Crop tool for the selected editor image. ImageResize already covers resize (drag
// handles) and repositioning (its floating left/center/right controller); this adds
// the missing "crop" action via Cropper.js (MIT, maintained) so we don't hand-roll a
// canvas cropper. The cropped result is written back inline as a base64 data-URI —
// matching how images are stored, so no upload/storage is involved.

const IMAGE_NODE = 'imageResize';

// True when a single image node is the active selection — gates the toolbar button.
export function isImageSelected(editor) {
  return editor.isActive(IMAGE_NODE);
}

// Derives the output mime from the current src so PNG transparency is preserved;
// falls back to PNG for non-data sources.
function mimeFromSrc(src) {
  const match = /^data:(image\/[a-z+]+);/i.exec(src || '');
  return match ? match[1] : 'image/png';
}

// Lazily builds the (single, reused) modal DOM. Buttons reuse the app's Bootstrap
// `.btn` classes; the overlay/stage are scoped under `.tr-crop` in editor.scss.
let modal = null;

function buildModal() {
  const overlay = document.createElement('div');
  overlay.className = 'tr-crop';

  const dialog = document.createElement('div');
  dialog.className = 'tr-crop__dialog';

  const head = document.createElement('div');
  head.className = 'tr-crop__head';
  const title = document.createElement('span');
  title.className = 'tr-crop__title';
  title.textContent = 'Cortar imagem';
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'tr-crop__close';
  closeButton.innerHTML = '&times;';
  head.appendChild(title);
  head.appendChild(closeButton);

  const stage = document.createElement('div');
  stage.className = 'tr-crop__stage';
  const image = document.createElement('img');
  image.className = 'tr-crop__img';
  stage.appendChild(image);

  const foot = document.createElement('div');
  foot.className = 'tr-crop__foot';
  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'btn btn-default';
  cancelButton.textContent = 'Cancelar';
  const applyButton = document.createElement('button');
  applyButton.type = 'button';
  applyButton.className = 'btn btn-green-500';
  applyButton.textContent = 'Aplicar';
  foot.appendChild(cancelButton);
  foot.appendChild(applyButton);

  dialog.appendChild(head);
  dialog.appendChild(stage);
  dialog.appendChild(foot);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  modal = { overlay, image, closeButton, cancelButton, applyButton, cropper: null };
  return modal;
}

function teardown() {
  if (!modal) return;
  if (modal.cropper) {
    modal.cropper.destroy();
    modal.cropper = null;
  }
  modal.overlay.classList.remove('is-open');
}

// Opens the cropper for the currently selected image. No-op when nothing (or a
// non-image) is selected.
export function openImageCropper(editor) {
  if (!isImageSelected(editor)) return;
  const source = editor.getAttributes(IMAGE_NODE).src;
  if (!source) return;

  const view = modal || buildModal();

  const apply = () => {
    if (!view.cropper) return;
    const canvas = view.cropper.getCroppedCanvas();
    if (canvas) {
      const dataUrl = canvas.toDataURL(mimeFromSrc(source));
      // Replace only the src; the natural size changes, so drop the stored width
      // and let ImageResize re-fit the cropped image.
      editor.chain().focus().updateAttributes(IMAGE_NODE, { src: dataUrl, width: null }).run();
    }
    teardown();
  };

  view.applyButton.onclick = apply;
  view.cancelButton.onclick = teardown;
  view.closeButton.onclick = teardown;
  view.overlay.onclick = (event) => {
    if (event.target === view.overlay) teardown();
  };

  if (view.cropper) view.cropper.destroy();
  view.image.src = source;
  view.overlay.classList.add('is-open');
  view.cropper = new Cropper(view.image, {
    viewMode: 1,
    autoCropArea: 1,
    background: false,
    responsive: true,
    checkCrossOrigin: false
  });
}
